import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tag, Plus, RefreshCcw, Trash2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const DEFAULT_FORM = { code: "", percent_off: 20, duration: "once", duration_in_months: 3, max_redemptions: "", expires_at: "" };

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [showDialog, setShowDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discount-codes");
      setCodes(data.codes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        percent_off: Number(form.percent_off),
        duration: form.duration,
      };
      if (form.duration === "repeating") body.duration_in_months = Number(form.duration_in_months);
      if (form.max_redemptions) body.max_redemptions = Number(form.max_redemptions);
      if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
      await api.post("/admin/discount-codes", body);
      toast.success(`Code ${body.code} created`);
      setShowDialog(false);
      setForm(DEFAULT_FORM);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (id) => {
    try {
      const { data } = await api.post(`/admin/discount-codes/${id}/toggle`);
      toast.success(data.active ? "Code activated" : "Code deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (id, code) => {
    if (!window.confirm(`Deactivate ${code}? (Stripe promotion codes can't be truly deleted — this just deactivates.)`)) return;
    try {
      await api.delete(`/admin/discount-codes/${id}`);
      toast.success("Deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivate failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-codes-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Admin</div>
          <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">Discount codes</h1>
          <p className="mt-1.5 text-sm sm:text-base text-slate-600">
            Public promotion codes. Users type these in on Stripe&apos;s hosted checkout page. Codes are stored in Stripe — this panel is a thin wrapper.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/users"><Button variant="outline" size="sm" data-testid="back-to-users"><ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Users</span></Button></Link>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-codes"><RefreshCcw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span></Button>
          <Button size="sm" onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-code-btn"><Plus className="w-4 h-4" /> New</Button>
        </div>
      </header>

      <Card className="border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>% off</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Redemptions</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
              ) : codes.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No discount codes yet. Click <span className="font-medium">New</span> to create one.</TableCell></TableRow>
              ) : (
                codes.map((c) => (
                  <TableRow key={c.id} data-testid={`code-row-${c.id}`}>
                    <TableCell><code className="font-mono text-sm font-medium text-slate-900">{c.code}</code></TableCell>
                    <TableCell className="tabular-nums">{c.percent_off ?? "—"}%</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {c.duration === "once" && "First payment only"}
                      {c.duration === "forever" && "Every payment (forever)"}
                      {c.duration === "repeating" && `First ${c.duration_in_months || "?"} months`}
                    </TableCell>
                    <TableCell className="tabular-nums text-xs">
                      {c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <Badge className={c.active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-zinc-200 text-zinc-700 hover:bg-zinc-200"}>
                        {c.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => toggle(c.id)} className="mr-2" data-testid={`toggle-${c.id}`}>
                        {c.active ? "Deactivate" : "Activate"}
                      </Button>
                      {c.active && (
                        <Button size="sm" variant="ghost" onClick={() => remove(c.id, c.code)} className="text-red-700" data-testid={`delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="new-code-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2"><Tag className="w-4 h-4" /> New discount code</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                placeholder="LAUNCH20"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                required
                pattern="[A-Z0-9_-]{2,32}"
                data-testid="code-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">A-Z, 0-9, _ and - only. 2-32 characters.</p>
            </div>
            <div>
              <Label htmlFor="pct">Percent off</Label>
              <Input id="pct" type="number" min="1" max="100" value={form.percent_off} onChange={(e) => setForm((f) => ({ ...f, percent_off: e.target.value }))} required data-testid="percent-input" />
            </div>
            <div>
              <Label htmlFor="dur">Duration</Label>
              <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v }))}>
                <SelectTrigger id="dur" data-testid="duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">First payment only</SelectItem>
                  <SelectItem value="repeating">First N months</SelectItem>
                  <SelectItem value="forever">Every payment (forever)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.duration === "repeating" && (
              <div>
                <Label htmlFor="dim">Duration (months)</Label>
                <Input id="dim" type="number" min="1" max="60" value={form.duration_in_months} onChange={(e) => setForm((f) => ({ ...f, duration_in_months: e.target.value }))} required data-testid="duration-months-input" />
              </div>
            )}
            <div>
              <Label htmlFor="maxr">Max redemptions (optional)</Label>
              <Input id="maxr" type="number" min="1" placeholder="Unlimited" value={form.max_redemptions} onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))} data-testid="max-redemptions-input" />
            </div>
            <div>
              <Label htmlFor="exp">Expires (optional)</Label>
              <Input id="exp" type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} data-testid="expires-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-code-confirm">
                {creating ? "Creating…" : "Create code"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
